import { useEffect, useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { communityAPI } from '../services/api'
import type { UserProfile } from '../types'
import toast from 'react-hot-toast'
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon,
  GlobeAltIcon,
  UserGroupIcon,
  DocumentTextIcon,
  TrophyIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import LoadingScreen from '../components/LoadingScreen'
import ImageUpload from '../components/ImageUpload'
import TrackRecordSection from '../components/trackRecord/TrackRecordSection'
import Tooltip from '../components/Tooltip'
import { PROFILE_GLOSSARY } from '../config/glossary'
import { NO_CLOSED_SIGNALS, formatWinRate, hasWinRate, pendingWinRateNote } from '../utils/winRate'
import { mediaUrl } from '../utils/mediaUrl'

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

const Profile = () => {
  const { user, updateUser } = useAuthStore()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  const [editForm, setEditForm] = useState({
    display_name: '',
    bio: '',
    location: '',
    website: '',
    avatar_url: '',
  })

  useEffect(() => {
    if (user?.id) {
      loadProfile(user.id)
    }
  }, [user?.id])

  const loadProfile = async (userId: number) => {
    try {
      setLoading(true)
      const data = await communityAPI.getProfile(userId)
      setProfile(data)
      setEditForm({
        display_name: data.display_name || '',
        bio: data.bio || '',
        location: data.location || '',
        website: data.website || '',
        avatar_url: data.avatar_url || '',
      })
    } catch (error) {
      console.error('Error loading profile:', error)
      toast.error('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await communityAPI.updateProfile(editForm)
      setProfile(updated)
      if (user && editForm.display_name) {
        updateUser({ full_name: editForm.display_name })
      }
      setIsEditing(false)
      toast.success('Profile updated successfully!')
    } catch (error) {
      console.error('Error saving profile:', error)
      toast.error('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (profile) {
      setEditForm({
        display_name: profile.display_name || '',
        bio: profile.bio || '',
        location: profile.location || '',
        website: profile.website || '',
        avatar_url: profile.avatar_url || '',
      })
    }
    setIsEditing(false)
  }

  if (loading) {
    return <LoadingScreen />
  }

  const displayName = profile?.display_name || user?.username || 'User'
  const initials = displayName.slice(0, 2).toUpperCase()

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition"
          >
            <PencilIcon className="h-4 w-4" />
            Edit Profile
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
            >
              <XMarkIcon className="h-4 w-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-primary-500 to-primary-700" />

        {/* Avatar & Basic Info */}
        <div className="px-6 pb-6">
          <div className="flex items-end justify-between -mt-12 mb-4">
            <div className="relative">
              {isEditing && editForm.avatar_url ? (
                <img
                  src={mediaUrl(editForm.avatar_url)}
                  alt={displayName}
                  className="h-24 w-24 rounded-full border-4 border-white object-cover"
                />
              ) : profile?.avatar_url ? (
                <img
                  src={mediaUrl(profile.avatar_url)}
                  alt={displayName}
                  className="h-24 w-24 rounded-full border-4 border-white object-cover"
                />
              ) : (
                <div className="h-24 w-24 rounded-full border-4 border-white bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-2xl">{initials}</span>
                </div>
              )}
            </div>
            {profile?.is_verified && (
              <div className="flex items-center gap-1 text-primary-600 text-sm font-medium">
                <ShieldCheckIcon className="h-5 w-5" />
                Verified
              </div>
            )}
          </div>

          {/* Avatar Upload in Edit Mode */}
          {isEditing && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profile Picture
              </label>
              <ImageUpload
                category="avatars"
                maxFiles={1}
                onUploadComplete={(urls) =>
                  setEditForm({ ...editForm, avatar_url: urls[0] || '' })
                }
              />
            </div>
          )}

          {isEditing ? (
            <div className="space-y-4">
              {/* Display Name */}
              <div>
                <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  id="display_name"
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  placeholder={user?.username}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Bio */}
              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  id="bio"
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder="Tell the community about yourself..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {editForm.bio.length}/500
                </p>
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  id="location"
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="City, Country"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  id="website"
                  type="url"
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                  placeholder="https://yourwebsite.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {profile?.display_name || user?.username}
              </h2>
              <p className="text-gray-500">@{user?.username}</p>

              {profile?.bio && (
                <p className="mt-3 text-gray-700 text-sm leading-relaxed">{profile.bio}</p>
              )}

              <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                {profile?.location && (
                  <span className="flex items-center gap-1">
                    <MapPinIcon className="h-4 w-4" />
                    {profile.location}
                  </span>
                )}
                {profile?.website && (
                  <a
                    href={profile.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary-600 hover:underline"
                  >
                    <GlobeAltIcon className="h-4 w-4" />
                    {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-5 text-center">
          <div className="flex justify-center mb-2">
            <UserGroupIcon className="h-6 w-6 text-primary-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {profile?.follower_count ?? 0}
          </p>
          <p className="text-sm text-gray-500">Followers</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5 text-center">
          <div className="flex justify-center mb-2">
            <UserGroupIcon className="h-6 w-6 text-primary-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {profile?.following_count ?? 0}
          </p>
          <p className="text-sm text-gray-500">Following</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5 text-center">
          <div className="flex justify-center mb-2">
            <DocumentTextIcon className="h-6 w-6 text-primary-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {profile?.post_count ?? 0}
          </p>
          <p className="text-sm text-gray-500">Posts</p>
        </div>
      </div>

      {/* Trading Stats */}
      {(profile?.total_signals !== undefined || profile?.win_rate !== undefined) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trading Performance</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {profile.total_signals !== undefined && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{profile.total_signals}</p>
                {/* The user's own creator signals; engine signals have no creator. */}
                <p className="text-sm text-gray-500">Your creator signals</p>
                <p className="mt-1 text-xs text-gray-400">Signal-engine results are on Track Record</p>
              </div>
            )}
            {profile.win_rate !== undefined && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p
                  className={`text-2xl font-bold ${
                    !hasWinRate(profile.win_rate, profile.closed_signals)
                      ? 'text-gray-400'
                      : profile.win_rate >= 60
                      ? 'text-success-600'
                      : 'text-danger-600'
                  }`}
                >
                  {formatWinRate(profile.win_rate, profile.closed_signals)}
                </p>
                <p className="text-sm text-gray-500">Win Rate</p>
                {!hasWinRate(profile.win_rate, profile.closed_signals) && (
                  <p className="text-xs text-gray-400">{pendingWinRateNote(profile.closed_signals, 'signal')}</p>
                )}
              </div>
            )}
            {profile.total_return !== undefined && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p
                  className={`text-2xl font-bold ${
                    profile.total_return == null
                      ? 'text-gray-400'
                      : profile.total_return >= 0
                      ? 'text-success-600'
                      : 'text-danger-600'
                  }`}
                >
                  {profile.total_return == null
                    ? '—'
                    : `${profile.total_return >= 0 ? '+' : ''}${profile.total_return.toFixed(2)}%`}
                </p>
                <p className="text-sm text-gray-500">
                  <Tooltip content={PROFILE_GLOSSARY.totalReturn}>Sum of trade returns</Tooltip>
                </p>
                {profile.total_return == null ? (
                  <p className="text-xs text-gray-400">{NO_CLOSED_SIGNALS}</p>
                ) : (
                  <p className="text-[10px] leading-tight text-gray-400">unsized, not account return</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Personal journal: self-reported trades, separate from the automated Track Record */}
      <TrackRecordSection />

      {/* Badges */}
      {profile?.badges && profile.badges.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrophyIcon className="h-5 w-5 text-warning-500" />
            Badges
          </h3>
          <div className="flex flex-wrap gap-2">
            {profile.badges.map((badge) => (
              <span
                key={badge}
                className="px-3 py-1 bg-primary-50 text-primary-700 text-sm font-medium rounded-full border border-primary-200"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Account Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Username</dt>
            <dd className="text-sm font-medium text-gray-900">@{user?.username}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Email</dt>
            <dd className="text-sm font-medium text-gray-900">{user?.email}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Member since</dt>
            <dd className="text-sm font-medium text-gray-900">
              {profile?.created_at
                ? formatDate(profile.created_at)
                : user?.created_at
                ? formatDate(user.created_at)
                : '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Account Status</dt>
            <dd>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.is_active
                    ? 'bg-success-50 text-success-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {user?.is_active ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

export default Profile
